import { useLocation, useParams } from "react-router-dom";
import { useHistory } from "react-router";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  SEARCH_QUERY_KEY,
  DESTINATIONS_FILTER_KEY,
  TAG_FILTER_KEY,
  TYPE_FILTER_KEY,
  VISIBILITY_FILTER_KEY,
  VisibilityFilterOptionValues,
} from "./components/Filters/constants";

const EVENTS_LIBRARY_BASE = "/events/library";

type StateFromQueryParams = {
  searchQuery: string;
  destinationFilterChoices: string[];
  tagFilterChoices: string[];
  typeFilterChoices: string[];
  visibilityFilterChoices: VisibilityFilterOptionValues[];
};

type UpdateStateFunctions = {
  createNewEvent: () => void;
  editEvent: (eventDefID: string) => void;
  closeEventEditor: () => void;
  setSearchQuery: (searchQuery: string) => void;
};

export type EventLibraryNavState = {
  selectedEvents: string[];
  setSelectedEvents: Dispatch<SetStateAction<string[]>>;
  eventDefID?: string;
} & StateFromQueryParams &
  UpdateStateFunctions;

function navigate(
  history: ReturnType<typeof useHistory>,
  searchParams: URLSearchParams,
  eventDefID: string | null = null
): void {
  const newSearchParams = searchParams.toString();

  const newPath =
    eventDefID !== null
      ? `${EVENTS_LIBRARY_BASE}/${eventDefID}`
      : EVENTS_LIBRARY_BASE;

  const newLocation =
    newSearchParams.length > 0 ? newPath + "?" + newSearchParams : newPath;

  history.push(newLocation);
}

export default function useEventLibraryNavState(): EventLibraryNavState {
  const location = useLocation();
  const history = useHistory();
  const urlParams = useParams<{ eventDefID?: string }>();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location,
  ]);

  // Set default visibility filter when none is provided when component mounts
  useEffect(() => {
    if (searchParams.size === 0) {
      searchParams.set(VISIBILITY_FILTER_KEY, "visible");

      navigate(history, searchParams);
    }
  }, [history, searchParams]);

  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const stateFromQueryParams = useMemo<StateFromQueryParams>(() => {
    const searchQuery = searchParams.get(SEARCH_QUERY_KEY) || "";
    const destinationFilterChoices =
      searchParams
        .get(DESTINATIONS_FILTER_KEY)
        ?.split(",")
        .filter((a) => a.length > 0) || [];
    const typeFilterChoices =
      searchParams.get(TYPE_FILTER_KEY)?.split(",") || [];
    const tagFilterChoices = searchParams.get(TAG_FILTER_KEY)?.split(",") || [];
    const visibilityFilterChoices = (searchParams
      .get(VISIBILITY_FILTER_KEY)
      ?.split(",") || []) as VisibilityFilterOptionValues[];

    return {
      searchQuery,
      destinationFilterChoices,
      tagFilterChoices,
      typeFilterChoices,
      visibilityFilterChoices,
    };
  }, [searchParams]);

  const setSearchQuery = useCallback(
    (searchQuery: string) => {
      searchParams.set(SEARCH_QUERY_KEY, searchQuery.trim());

      navigate(history, searchParams);
    },
    [history, searchParams]
  );

  const editEvent = useCallback(
    (eventDefID: string) => {
      navigate(history, searchParams, eventDefID);
    },
    [history, searchParams]
  );

  const createNewEvent = useCallback(() => editEvent("new"), [editEvent]);

  const closeEventEditor = useCallback(() => {
    navigate(history, searchParams);
  }, [history, searchParams]);

  return {
    ...stateFromQueryParams,
    createNewEvent,
    editEvent,
    closeEventEditor,
    setSearchQuery,
    eventDefID: urlParams.eventDefID,
    selectedEvents,
    setSelectedEvents,
  };
}
